import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, Save, Trash2 } from 'lucide-react'
import { toast } from '@atrvd/saas-ui'
import {
  FILE_UPLOAD_LIMITS,
  canUpdateCompanyName,
  canViewOrganizationSettings,
  organizationSettingsSchema,
  type OrganizationSettingsInput,
} from '@dcp/shared'
import {
  useOrganizationDetails,
  useUpdateOrganizationSettings,
  useUploadOrganizationLogo,
  useDeleteOrganizationLogo,
} from '@/hooks/useOrganizationSettings'
import { useProfile } from '@/hooks/useProfile'
import { useOrganization } from '@/contexts/OrganizationContext'
import {
  Button,
  Input,
  Label,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PageHeader,
  AvatarUpload,
} from '@atrvd/saas-ui'

/**
 * Logo section component
 * Uses AvatarUpload component for consistent upload experience
 * Handles logo preview locally (no immediate upload)
 */
function LogoSection({
  currentLogoUrl,
  organizationName,
  pendingFile,
  pendingDelete,
  onFileSelect,
  onMarkForDelete,
  isDisabled,
}: {
  currentLogoUrl: string | null | undefined
  organizationName: string
  pendingFile: File | null
  pendingDelete: boolean
  onFileSelect: (file: File | null) => void
  onMarkForDelete: () => void
  isDisabled: boolean
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  // Create/cleanup preview URL when pending file changes
  useEffect(() => {
    if (pendingFile) {
      const objectUrl = URL.createObjectURL(pendingFile)
      setPreviewUrl(objectUrl)
      return () => URL.revokeObjectURL(objectUrl)
    } else {
      setPreviewUrl(null)
    }
  }, [pendingFile])

  /**
   * Handle file selection from AvatarUpload
   */
  const handleImageChange = (file: File) => {
    onFileSelect(file)
  }

  /**
   * Mark logo for deletion (will be applied on Save)
   */
  const handleDelete = () => {
    onFileSelect(null)
    onMarkForDelete()
  }

  // Show: pending file preview > current logo (if not marked for delete) > nothing
  const displayUrl = previewUrl || (pendingDelete ? null : currentLogoUrl)
  const showDeleteButton = (currentLogoUrl && !pendingDelete) || pendingFile

  // Generate fallback initials from organization name
  const fallback = organizationName
    ?.split(' ')
    .map((word) => word.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('') || 'O'

  return (
    <div className="space-y-4">
      <Label>Organization Logo</Label>
      <AvatarUpload
        currentImage={displayUrl || undefined}
        fallback={fallback}
        onImageChange={handleImageChange}
        size="lg"
        disabled={isDisabled}
        helpText={`Square image, max ${FILE_UPLOAD_LIMITS.ORGANIZATION_LOGO.MAX_SIZE_LABEL}. Changes will be saved when you click Save.`}
      />

      {showDeleteButton && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleDelete}
          disabled={isDisabled}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Remove Logo
        </Button>
      )}
    </div>
  )
}

/**
 * Organization settings form section
 * Allows admins to update organization name and logo
 * All changes are saved together when clicking Save
 */
function OrganizationSettingsSection() {
  const { currentOrganization } = useOrganization()
  const { data: profile } = useProfile()
  const { data: organizationDetails, isLoading } = useOrganizationDetails(
    currentOrganization?.id
  )

  // Only super_admin and org_owner may edit the organization name (DCP-158)
  const canEditName = !!profile && canUpdateCompanyName(profile.role)
  const updateSettings = useUpdateOrganizationSettings()
  const uploadLogo = useUploadOrganizationLogo()
  const deleteLogo = useDeleteOrganizationLogo()

  // Pending changes state (applied only on Save)
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null)
  const [pendingLogoDelete, setPendingLogoDelete] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<OrganizationSettingsInput>({
    resolver: zodResolver(organizationSettingsSchema),
    defaultValues: { name: '' },
  })

  // Reset form and pending states when organization details load
  useEffect(() => {
    if (organizationDetails) {
      reset({ name: organizationDetails.name })
      setPendingLogoFile(null)
      setPendingLogoDelete(false)
    }
  }, [organizationDetails, reset])

  // Check if there are any pending changes
  const hasLogoChanges = pendingLogoFile !== null || pendingLogoDelete
  const hasChanges = isDirty || hasLogoChanges

  /**
   * Handle form submission
   * Saves name and logo changes together
   */
  const onSubmit = async (data: OrganizationSettingsInput) => {
    if (!currentOrganization?.id) return

    setIsSaving(true)

    try {
      let newLogoUrl: string | null | undefined = undefined
      const oldLogoUrl = organizationDetails?.logoUrl

      // Upload new logo first (before DB update) — reversible on failure
      if (pendingLogoFile) {
        newLogoUrl = await uploadLogo.mutateAsync({
          organizationId: currentOrganization.id,
          file: pendingLogoFile,
        })
      } else if (pendingLogoDelete) {
        newLogoUrl = null
      }

      // Update organization settings (name and/or logo URL)
      try {
        await updateSettings.mutateAsync({
          organizationId: currentOrganization.id,
          name: data.name,
          ...(newLogoUrl !== undefined && { logoUrl: newLogoUrl }),
        })
      } catch (settingsError) {
        // Rollback: if we uploaded a new logo but settings update failed,
        // delete the uploaded logo to avoid orphaned files
        if (newLogoUrl && pendingLogoFile) {
          try {
            await deleteLogo.mutateAsync({ logoUrl: newLogoUrl })
          } catch (rollbackError) {
            console.error('Failed to rollback logo upload:', rollbackError)
          }
        }
        throw settingsError
      }

      // DB update succeeded — now safe to delete old logo from storage.
      // Done after DB update so we never lose the file while DB still references it.
      if (pendingLogoDelete && oldLogoUrl) {
        try {
          await deleteLogo.mutateAsync({ logoUrl: oldLogoUrl })
        } catch (deleteError) {
          // Non-critical: DB is consistent, just an orphaned storage file
          console.error('Failed to delete old logo from storage:', deleteError)
        }
      }

      // Clear pending states
      setPendingLogoFile(null)
      setPendingLogoDelete(false)

      toast.success('Organization settings updated successfully')
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to update organization settings'
      toast.error(message)
    } finally {
      setIsSaving(false)
    }
  }

  /**
   * Handle file selection (local only, not uploaded yet)
   */
  const handleFileSelect = (file: File | null) => {
    setPendingLogoFile(file)
    // If selecting a new file, clear the delete flag
    if (file) {
      setPendingLogoDelete(false)
    }
  }

  /**
   * Mark logo for deletion (will be applied on Save)
   */
  const handleMarkForDelete = () => {
    setPendingLogoDelete(true)
    setPendingLogoFile(null)
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex h-32 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Organization Settings</CardTitle>
        <CardDescription>Update organization information and branding</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Organization name */}
          <div className="space-y-2">
            <Label htmlFor="name">Organization Name</Label>
            <Input
              id="name"
              placeholder="Acme Corporation"
              disabled={!canEditName}
              {...register('name')}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
            {!canEditName && (
              <p className="text-xs text-muted-foreground">
                Only the account owner can change the organization name.
              </p>
            )}
          </div>

          {/* Logo section */}
          <LogoSection
            currentLogoUrl={organizationDetails?.logoUrl}
            organizationName={organizationDetails?.name || ''}
            pendingFile={pendingLogoFile}
            pendingDelete={pendingLogoDelete}
            onFileSelect={handleFileSelect}
            onMarkForDelete={handleMarkForDelete}
            isDisabled={isSaving}
          />

          {/* Save button */}
          <Button type="submit" disabled={!hasChanges || isSaving}>
            {isSaving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save Changes
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

/**
 * Organization settings page
 * Allows admins to manage organization name and logo
 * Requires admin role - redirects to profile settings if not authorized
 */
export function SettingsOrganizationPage() {
  const { data: profile, isLoading } = useProfile()

  // Show loading state while checking permissions
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Redirect if user cannot view the Organization Settings page (DCP-158)
  if (!profile || !canViewOrganizationSettings({ role: profile.role, productRole: profile.productRole })) {
    return <Navigate to="/settings/profile" replace />
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Organization Settings"
        description="Manage organization information and branding"
      />

      <div className="grid gap-6">
        <OrganizationSettingsSection />
      </div>
    </div>
  )
}
